// ===== 5. Main Technical Sections Component (Updated) =====
// src/components/TechnicalSectionsWithAI.tsx

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Fade, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { RootState } from 'src/redux-store/appStore';
import { useAISubcodeRecommendations } from '../../../../services/ai/useAISubcodeRecommendations';
import AIRecommendationAccordion from '../../../../services/ai/AIRecommendationAccordion';
import SubcodeAccordion from './SubcodeAccordion';

// Import your existing subcode components
import ElectricalSubcode from './ElectricalSubcode/index';
import BuildingSubcode from './BuildingSubcode';
import PlumbingSubcode from './PlumbingSubcode';
import FireSubcode from './FireSubcode';
import ChimneySubcode from './ChimneySubcode';
import MechanicalSubcode from './MechanicalSubcode';

// Your existing imports
import ApartmentIcon from '@mui/icons-material/Apartment';
import FireHydrantAltIcon from '@mui/icons-material/FireHydrantAlt';
import PowerIcon from '@mui/icons-material/Power';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import BuildIcon from '@mui/icons-material/Build';
import FireplaceIcon from '@mui/icons-material/Fireplace';

import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import getBaseUrl from 'src/utils/getBaseUrl';
import SharedFormsHistoryModal from './TechnicalSectionModals/SharedFormsHistoryModal';
import UCCFormStatusSummary from './TechnicalSectionModals/UCCFormStatusSummary';

interface TechnicalSectionsProps {
  applicationId: number;
}

const TechnicalSectionsWithAI: React.FC<TechnicalSectionsProps> = ({ applicationId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get form data from Redux store
  const formData = useSelector((state: RootState) => state.constructionFormSlice.formData);

  const currentPermitDetails = useSelector(
    (store: RootState) => store.currentPermit.currentPermitDetails
  );
const isUser = currentPermitDetails?.currentPermitUserRole === 'Vendor';

  // AI recommendations hook - only call when isUser is true
  const aiHookResult = useAISubcodeRecommendations(isUser ? formData : null);
  const { recommendations, loading: aiLoading, error: aiError } = isUser ? aiHookResult : { recommendations: null, loading: false, error: null };

  // State for selected subcodes
  const [selectedSubcodes, setSelectedSubcodes] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string | false>(false);
  
  // Your existing state variables
  const [open, setOpen] = useState(false);
  const [receiverName, setReceiverName] = useState('');
  const [receiverEmail, setReceiverEmail] = useState('');
  const [selectedUCCFormInfo, setSelectedUCCFormInfo] = useState(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [formStatuses, setFormStatuses] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState(null);

  // Memoize fetchFormStatuses to prevent recreation on every render
  const fetchFormStatuses = useCallback(async () => {
    try {
      const response = await httpRequest(
        'GET',
        `/construction/${applicationId}/check_ucc_form_status`
      );

      if (response.status === 'success') {
        if (
          response.message === 'No UCC forms found' ||
          (Array.isArray(response.data) && response.data.length === 0)
        ) {
          setFormStatuses({});
        } else {
          setFormStatuses(response.data);
        }
      } else {
        setError('Failed to fetch form statuses');
      }
    } catch (err) {
      setError('Error fetching form statuses');
      console.error(err);
    }
  }, [applicationId]);

  // Memoize allSubCodes to prevent recreation on every render
  const allSubCodes = useMemo(() => [
    {
      id: 1,
      label: 'BUILDING',
      icon: <ApartmentIcon />,
      content: (
        <BuildingSubcode
          applicationId={applicationId}
          setExpanded={setExpanded}
          fetchFormStatuses={fetchFormStatuses}
        />
      )
    },
    {
      id: 2,
      label: 'ELECTRICAL',
      icon: <PowerIcon />,
      content: (
        <ElectricalSubcode
          applicationId={applicationId}
          setExpanded={setExpanded}
          fetchFormStatuses={fetchFormStatuses}
        />
      )
    },
    {
      id: 3,
      label: 'PLUMBING',
      icon: <PlumbingIcon />,
      content: (
        <PlumbingSubcode
          applicationId={applicationId}
          setExpanded={setExpanded}
          fetchFormStatuses={fetchFormStatuses}
        />
      )
    },
    {
      id: 4,
      label: 'FIRE PROTECTION',
      icon: <FireHydrantAltIcon />,
      content: (
        <FireSubcode
          applicationId={applicationId}
          setExpanded={setExpanded}
          fetchFormStatuses={fetchFormStatuses}
        />
      )
    },
    {
      id: 5,
      label: 'MECHANICAL',
      icon: <BuildIcon />,
      content: (
        <MechanicalSubcode
          applicationId={applicationId}
          setExpanded={setExpanded}
          fetchFormStatuses={fetchFormStatuses}
        />
      )
    },
    {
      id: 7,
      label: 'CHIMNEY',
      icon: <FireplaceIcon />,
      content: (
        <ChimneySubcode
          applicationId={applicationId}
          setExpanded={setExpanded}
          fetchFormStatuses={fetchFormStatuses}
        />
      )
    }
  ], [applicationId, fetchFormStatuses]);

  // Memoize handleSubcodeToggle to prevent recreation on every render
  const handleSubcodeToggle = useCallback((subcodeType: string, isSelected: boolean) => {
    const matchingSubcode = allSubCodes.find(subcode => 
      subcode.label.toLowerCase() === subcodeType.toLowerCase() ||
      subcode.label.toLowerCase().replace(/\s+/g, '') === subcodeType.toLowerCase().replace(/\s+/g, '')
    );
    
    const normalizedSubcodeType = matchingSubcode ? matchingSubcode.label : subcodeType;
    
    if (isSelected) {
      setSelectedSubcodes(prev => {
        const filtered = prev.filter(s => 
          s.toLowerCase() !== normalizedSubcodeType.toLowerCase()
        );
        return [...filtered, normalizedSubcodeType];
      });
    } else {
      setSelectedSubcodes(prev => prev.filter(s => 
        s.toLowerCase() !== normalizedSubcodeType.toLowerCase()
      ));
    }
  }, [allSubCodes]);

  // Auto-select required subcodes when recommendations change
  useEffect(() => {
    if (isUser && recommendations?.recommendations) {
      const requiredSubcodes = recommendations.recommendations
        .filter(rec => rec.required)
        .map(rec => rec.subcodeType);
      
      setSelectedSubcodes(prev => {
        const newSelection = [...prev];
        
        requiredSubcodes.forEach(aiSubcode => {
          const isAlreadySelected = newSelection.some(selected => 
            selected.toLowerCase() === aiSubcode.toLowerCase()
          );
          
          if (!isAlreadySelected) {
            const matchingSubcode = allSubCodes.find(subcode => 
              subcode.label.toLowerCase() === aiSubcode.toLowerCase() ||
              subcode.label.toLowerCase().replace(/\s+/g, '') === aiSubcode.toLowerCase().replace(/\s+/g, '')
            );
            
            if (matchingSubcode) {
              newSelection.push(matchingSubcode.label);
            } else {
              newSelection.push(aiSubcode);
            }
          }
        });
        
        return newSelection;
      });
    }
  }, [isUser ,recommendations, allSubCodes]);

  // Get AI recommendation for a specific subcode
  const getAIRecommendation = useCallback((subcodeLabel: string) => {
    if (!isUser || !recommendations?.recommendations) return undefined;
    
    return recommendations.recommendations.find(rec => 
      rec.subcodeType.toLowerCase() === subcodeLabel.toLowerCase() ||
      rec.subcodeType.toLowerCase().replace(/\s+/g, '') === subcodeLabel.toLowerCase().replace(/\s+/g, '')
    );
  }, [isUser, recommendations]);

  // Check if subcode is selected (either manually or AI recommended)
  // const isSubcodeSelected = useCallback((subcodeLabel: string) => {
  //   return selectedSubcodes.some(selected => 
  //     selected.toLowerCase() === subcodeLabel.toLowerCase() ||
  //     selected.toLowerCase().replace(/\s+/g, '') === subcodeLabel.toLowerCase().replace(/\s+/g, '')
  //   );
  // }, [selectedSubcodes]);

  // Group subcodes into AI recommended and others
  const { aiRecommendedSubcodes, otherSubcodes } = useMemo(() => {
    if (!isUser) {
      return { aiRecommendedSubcodes: [], otherSubcodes: allSubCodes };
    }

    const aiRecommended: typeof allSubCodes = [];
    const others: typeof allSubCodes = [];
    
    allSubCodes.forEach(subcode => {
      const aiRec = getAIRecommendation(subcode.label);
      if (aiRec) {
        aiRecommended.push(subcode);
      } else {
        others.push(subcode);
      }
    });
    
    // Sort AI recommended by priority (required first, then by priority number)
    aiRecommended.sort((a, b) => {
      const aRec = getAIRecommendation(a.label)!;
      const bRec = getAIRecommendation(b.label)!;
      
      if (aRec.required && !bRec.required) return -1;
      if (!aRec.required && bRec.required) return 1;
      return aRec.priority - bRec.priority;
    });
    
    return { aiRecommendedSubcodes: aiRecommended, otherSubcodes: others };
  }, [isUser, allSubCodes, getAIRecommendation]);

  // Your existing functions with useCallback
  const handleAccordionChange = useCallback((panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  }, []);

  const handleClickOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSend = useCallback(async () => {
    const baseURL = getBaseUrl();
    setLoading(true);
    try {
      const response = await httpRequest('POST', '/construction/ucc_form_share', {
        ApplicationID: applicationId,
        FormTypeID: selectedUCCFormInfo?.id,
        UCCFormID: 1,
        UCCFormName: selectedUCCFormInfo?.label,
        ProfessionalEmail: receiverEmail,
        ProfessionalName: receiverName,
        BaseUrl: baseURL + '/ucc-form',
        IsGuest: true
      });
      toast.success(`Form shared successfully with ${response?.data?.message}`);

      if (applicationId) await fetchFormStatuses();
    } catch (err) {
      toast.error('Failed to share form');
    }
    setLoading(false);
    setOpen(false);
  }, [applicationId, selectedUCCFormInfo, receiverEmail, receiverName, fetchFormStatuses]);

  // Load form statuses only once when applicationId changes
  useEffect(() => {
    if (applicationId) {
      setLoading(true);
      fetchFormStatuses().finally(() => setLoading(false));
    }
  }, [applicationId, fetchFormStatuses]);

  const renderSubcodeSection = (subcodes: typeof allSubCodes, title: string, subtitle?: string) => {
    if (subcodes.length === 0) return null;

    return (
      <Box mb={4}>
        <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {subtitle}
          </Typography>
        )}
        
        {subcodes.map((subcode, index) => {
          const panelId = `${title.toLowerCase().replace(' ', '-')}-panel-${index}`;
          const formType = subcode.label;
          const formData1 = formStatuses[formType];
          const aiRecommendation = getAIRecommendation(subcode.label);
          
          let statusComponent = null;
          if (formData1 && formData1.length > 0) {
            const formInfo = formData1[0];
            const pendingCount = formInfo.Pending || 0;
            
            if (pendingCount > 0) {
              statusComponent = <UCCFormStatusSummary status="Pending" count={pendingCount} />;
            } else {
              statusComponent = <UCCFormStatusSummary status="Completed" count={0} />;
            }
          }

          return (
            <SubcodeAccordion
              key={subcode.label}
              subcode={subcode}
              aiRecommendation={aiRecommendation}
              statusComponent={statusComponent}
              expanded={expanded === panelId}
              panelId={panelId}
              onAccordionChange={handleAccordionChange}
              onSendClick={() => {
                setSelectedUCCFormInfo(subcode);
                handleClickOpen();
              }}
              onHistoryClick={() => {
                setSelectedUCCFormInfo(subcode);
                setHistoryModalOpen(true);
              }}
            />
          );
        })}
      </Box>
    );
  };

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Box sx={{ width: '100%' }}>
      {/* AI Recommendation Accordion */}
      {isUser && <AIRecommendationAccordion
        recommendations={recommendations}
        loading={aiLoading}
        error={aiError}
        selectedSubcodes={selectedSubcodes}
        onSubcodeToggle={handleSubcodeToggle}
        // ApplicationStatus={formData?.ApplicationStatus}
      />}

      {/* Subcodes Sections */}
      <Fade in={true} timeout={500}>
        <Box>
          {/* AI Recommended Subcodes */}
          {isUser && renderSubcodeSection(
            aiRecommendedSubcodes,
            'Recommended Subcodes',
            'These subcodes are specifically recommended for your project'
          )}

          {/* Other Available Subcodes */}
          {isUser && renderSubcodeSection(
            otherSubcodes,
            '📋 Additional Available Subcodes',
            'Select any additional subcodes that may be required for your specific project'
          )}

          {!isUser && renderSubcodeSection(
            allSubCodes,
            '📋 Review the subcodes filled by the applicant',
            'Select any additional subcodes that may be required for the project'
          )}

          {/* Message when no subcodes available */}
          {allSubCodes.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No subcodes available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please contact the permit office for assistance.
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>

      {/* Your existing modals */}
      <SharedFormsHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        applicationId={applicationId}
        formTypeId={selectedUCCFormInfo?.id}
        formType={selectedUCCFormInfo?.label}
      />

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { m: isMobile ? 1 : 3 }
        }}
      >
        <DialogTitle variant="h4">Send to Receiver</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Receiver Name"
            type="text"
            fullWidth
            variant="outlined"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Receiver Email"
            type="email"
            fullWidth
            variant="outlined"
            value={receiverEmail}
            onChange={(e) => setReceiverEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSend} disabled={!receiverName || !receiverEmail}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicalSectionsWithAI;